import {publishEvent} from '@udemy-v2/event-tracking';
import {Icon} from '@udemy-v2/icons';
import {IconButton} from '@udemy-v2/react-core-components';
import {Dropdown} from '@udemy-v2/react-menu-components';
import classNames from 'classnames';
import React, {useState, MouseEvent} from 'react';

import {ReportAbuse} from '../../report-abuse/report-abuse.react-component';
import {ReviewReportEvent} from '../../../events/events';

import styles from './review-report.module.scss';
import {useI18n} from '@udemy-v2/i18n';

export interface ReviewReportProps {
    reviewId: number;
    frontendTrackingId?: string;
    reviewableObjectId: number;
    reviewableObjectType: string;
    isFeaturedReview?: boolean;
    shouldUseDeattachedDropdown?: boolean;
    shouldClosePopper?: boolean;
    placement?: 'bottom-end' | 'top-end';
    reviewAuthorName: string;
}

export const ReviewReport = ({
    reviewId,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    isFeaturedReview = false,
    shouldUseDeattachedDropdown = false,
    shouldClosePopper = false,
    placement = 'bottom-end',
    reviewAuthorName,
}: ReviewReportProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const {gettext, interpolate} = useI18n();

    // Track previous shouldClosePopper value to detect changes
    const prevShouldClosePopperRef = React.useRef(shouldClosePopper);

    // Use effect to handle closing the popper when shouldClosePopper transitions to true
    React.useEffect(() => {
        if (shouldClosePopper && !prevShouldClosePopperRef.current && isOpen) {
            setIsOpen(false);
        }
        prevShouldClosePopperRef.current = shouldClosePopper;
    }, [shouldClosePopper, isOpen]);

    const handleReportClick = (event?: MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation();
        if (frontendTrackingId) {
            publishEvent(
                new ReviewReportEvent({
                    courseTrackingId: frontendTrackingId,
                    reviewId,
                    isFeatured: isFeaturedReview,
                    reviewableObjectId,
                    reviewableObjectType,
                }),
            );
        }
    };

    return (
        <Dropdown
            useDrawer={false}
            onToggle={setIsOpen}
            menuWidth="auto"
            detachFromTarget={shouldUseDeattachedDropdown}
            placement={placement}
            renderTrigger={(props) => (
                <IconButton udStyle="ghost" {...props}>
                    <Icon
                        glyph="more"
                        label={interpolate(
                            gettext('Additional actions for review by %(name)s'),
                            {name: reviewAuthorName},
                            true,
                        )}
                        size="small"
                        color="neutral"
                    />
                </IconButton>
            )}
        >
            <Dropdown.Menu>
                <Dropdown.MenuItem componentClass="div" className={styles['menu-item']}>
                    <ReportAbuse
                        className={classNames('ud-heading-sm', styles['report-abuse-label'])}
                        objectId={reviewId}
                        objectType="coursereview"
                        onClick={handleReportClick}
                        size="medium"
                        text={gettext('Report')}
                    />
                </Dropdown.MenuItem>
            </Dropdown.Menu>
        </Dropdown>
    );
};
